import { ErrorBoundary } from '@bookjane2/bookjane-design-library';
import { DesktopPageHeader } from 'components/DesktopPageHeader';
import { Flex } from 'components/Flex';
import { MobilePageHeader } from 'components/MobilePageHeader/MobilePageHeader';
import { PageWrapper } from 'components/PageWrapper';
import { ReportsNavigation } from './components/ReportsNavigation';
import { useDimensionsRef } from 'hooks';
import { FC, useEffect } from 'react';
import { Redirect, Route, RouteProps, Switch, useRouteMatch } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { MountTransition } from 'routes';
import { useTranslation } from 'hooks/useTranslation';
import { isPremiumDashboardTierEnabledSelector } from 'store/selectors/featureFlagSelectors/isPremiumDashboardTierEnabledSelector';
import { isDashboardTierNoneSelector } from 'store/selectors/featureFlagSelectors/isDashboardTierNoneSelector';
import { getDefaultDashboardPath } from 'pages/ReportsPage/ReportsPage.constants';
import { OverviewDashboard } from 'pages/ReportsPage/components/AnalyticsDashboards/OverviewDashboard';
import { FulfillmentDashboard } from 'pages/ReportsPage/components/AnalyticsDashboards/FulfillmentDashboard';
import { LaborCostDashboard } from 'pages/ReportsPage/components/AnalyticsDashboards/LaborCostDashboard';
import { LaborProductivityDashboard } from 'pages/ReportsPage/components/AnalyticsDashboards/LaborProductivityDashboard';
import { EmployeeEngagementDashboard } from 'pages/ReportsPage/components/AnalyticsDashboards/EmployeeEngagementDashboard';
import { OvertimeDashboard } from 'pages/ReportsPage/components/AnalyticsDashboards/OvertimeDashboard';
import { DailyOvertimeDashboard } from 'pages/ReportsPage/components/AnalyticsDashboards/DailyOvertimeDashboard';
import { AgencyDashboard } from 'pages/ReportsPage/components/AnalyticsDashboards/AgencyDashboard';
import {
  ReportsPageViewTemplate,
  AnalyticsHeaderTag,
  HeaderRightGroup,
  DataFreshnessLabel,
} from 'pages/ReportsPage/ReportsPage.styled';
import { ReportsDataProvider } from 'pages/ReportsPage/ReportsPage.data.context';
import { DataLockedView } from 'pages/ReportsPage/components/DataLockedView';
import { AnalyticsFilterViewProvider } from 'pages/ReportsPage/store/analyticsFilterView.context';
import { useReportsDataHealthStore } from 'pages/ReportsPage/store/reportsDataHealth.store';
import { useDocumentTitle } from 'hooks/useDocumentTitle';

function formatLastRefresh(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  });
}

const ReportsPage: FC<RouteProps> = () => {
  const { t } = useTranslation();
  useDocumentTitle(t('page_titles:REPORTS'));
  const isDashboardTierNone = useSelector(isDashboardTierNoneSelector);

  if (isDashboardTierNone) return <Redirect to="/dashboard" />;

  return (
    <ReportsDataProvider>
      <ReportsPageContent />
    </ReportsDataProvider>
  );
};

const ReportsPageContent: FC = () => {
  const [ref, { width: x }] = useDimensionsRef<HTMLDivElement>();
  const { t } = useTranslation();
  const fetchHealth = useReportsDataHealthStore((s) => s.fetchHealth);
  const isRefreshing = useReportsDataHealthStore((s) => s.isRefreshing);
  const lastRefreshAt = useReportsDataHealthStore((s) => s.lastRefreshAt);

  useEffect(() => {
    fetchHealth();
  }, [fetchHealth]);

  const formattedRefresh = lastRefreshAt ? formatLastRefresh(lastRefreshAt) : '';
  const freshnessText = isRefreshing
    ? 'Updating data…'
    : formattedRefresh
    ? `Data as of ${formattedRefresh}`
    : '';

  return (
    <PageWrapper>
      <DesktopPageHeader title={t('reports:REPORTS_AND_ANALYTICS')}>
        <HeaderRightGroup>
          {freshnessText && <DataFreshnessLabel>{freshnessText}</DataFreshnessLabel>}
          <AnalyticsHeaderTag>
            <span>{t('reports:ANALYTICS')}</span>
          </AnalyticsHeaderTag>
        </HeaderRightGroup>
      </DesktopPageHeader>
      <MobilePageHeader title={t('reports:REPORTS_AND_ANALYTICS')} />
      <Flex width="100%" height="100%">
        <ReportsNavigation ref={ref} />
        <PageViewTemplate x={x} />
      </Flex>
    </PageWrapper>
  );
};

const PageViewTemplate: FC<{ x: number }> = ({ x }) => {
  const { path } = useRouteMatch();
  const isPremiumDashboardTierEnabled = useSelector(isPremiumDashboardTierEnabledSelector);
  const dataVersion = useReportsDataHealthStore((s) => s.dataVersion);
  const defaultPath = getDefaultDashboardPath(isPremiumDashboardTierEnabled) ?? `${path}/overview`;

  return (
    <ReportsPageViewTemplate x={x} flexDirection="column" isPendingApproval={false}>
      <ErrorBoundary componentName="ReportsModule" suppressMessage showLogo={false}>
        <MountTransition key={dataVersion} height="100%">
          <Switch>
            <Redirect exact from={`${path}`} to={defaultPath} />
            <Route
              exact
              path={`${path}/overview`}
              render={() => (
                <AnalyticsFilterViewProvider view="overview">
                  <OverviewDashboard />
                </AnalyticsFilterViewProvider>
              )}
            />
            <Route
              exact
              path={`${path}/fulfillment`}
              render={() => (
                <AnalyticsFilterViewProvider view="fulfillment">
                  <FulfillmentDashboard />
                </AnalyticsFilterViewProvider>
              )}
            />
            <Route
              exact
              path={`${path}/labor-cost`}
              render={() => (
                <AnalyticsFilterViewProvider view="labor-cost">
                  <LaborCostDashboard />
                </AnalyticsFilterViewProvider>
              )}
            />
            <Route
              exact
              path={`${path}/overtime-custom`}
              render={() => (
                <AnalyticsFilterViewProvider view="overtime-custom">
                  <OvertimeDashboard />
                </AnalyticsFilterViewProvider>
              )}
            />
            <Route
              exact
              path={`${path}/overtime-daily`}
              render={() => (
                <AnalyticsFilterViewProvider view="overtime-daily">
                  <DailyOvertimeDashboard />
                </AnalyticsFilterViewProvider>
              )}
            />
            <Route
              exact
              path={`${path}/agency`}
              render={() => (
                <AnalyticsFilterViewProvider view="agency">
                  <AgencyDashboard />
                </AnalyticsFilterViewProvider>
              )}
            />
            <Route
              exact
              path={`${path}/employee-engagement`}
              render={() => (
                <AnalyticsFilterViewProvider view="employee-engagement">
                  <EmployeeEngagementDashboard />
                </AnalyticsFilterViewProvider>
              )}
            />
            <Route
              exact
              path={`${path}/labor-productivity`}
              render={() => (
                <AnalyticsFilterViewProvider view="labor-productivity">
                  <LaborProductivityDashboard />
                </AnalyticsFilterViewProvider>
              )}
            />
          </Switch>
        </MountTransition>
      </ErrorBoundary>
      <DataLockedView x={x} />
    </ReportsPageViewTemplate>
  );
};

export { ReportsPage };
